import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Switch } from '@/components/ui/switch'
import { MessageSquare } from 'lucide-react'
import type { Control, UseFormWatch } from 'react-hook-form'
import type { AgentDirection } from '@super/shared-types'
import {
  INBOUND_POST_CALL_SMS_TEMPLATE,
  OUTBOUND_POST_CALL_SMS_TEMPLATE,
} from '@super/shared-types'
import type { FormValues } from './agent-detail.schema'
import { VariableAutocompleteTextarea } from './VariableAutocompleteTextarea'

type SmsFollowUpCardProps = {
  control: Control<FormValues>
  watch: UseFormWatch<FormValues>
  smsFollowUpInputRef: React.MutableRefObject<HTMLTextAreaElement | null>
  direction: AgentDirection
}

export function SmsFollowUpCard({
  control,
  watch,
  smsFollowUpInputRef,
  direction,
}: SmsFollowUpCardProps) {
  const defaultMessage =
    direction === 'OUTBOUND'
      ? OUTBOUND_POST_CALL_SMS_TEMPLATE
      : INBOUND_POST_CALL_SMS_TEMPLATE
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquare className="w-4 h-4" />
          SMS follow-up
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <FormField
          control={control}
          name="isSmsFollowUpEnabled"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center gap-4">
                <FormLabel className="mb-0">
                  Send SMS after every call
                </FormLabel>
                <FormControl>
                  <Switch
                    checked={field.value ?? false}
                    onCheckedChange={checked => {
                      field.onChange(checked)
                      if (checked) {
                        requestAnimationFrame(() => {
                          smsFollowUpInputRef.current?.focus()
                        })
                      }
                    }}
                  />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="smsFollowUpText"
          render={({ field }) => {
            const { ref, ...fieldProps } = field
            return (
              <FormItem>
                <FormLabel>SMS message</FormLabel>
                <FormControl>
                  <VariableAutocompleteTextarea
                    {...fieldProps}
                    ref={e => {
                      ref(e)
                      smsFollowUpInputRef.current = e
                    }}
                    placeholder={defaultMessage}
                    className="min-h-[100px]"
                    disabled={!watch('isSmsFollowUpEnabled')}
                  />
                </FormControl>
                <p className="text-xs text-muted-foreground mt-1">
                  Tip: add a fallback with{' '}
                  <code className="text-xs">
                    {"{{ listingURL | 'https://your-site.com/listings' }}"}
                  </code>
                </p>
                {!field.value?.trim() && (
                  <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                    <p className="mb-1 font-medium text-foreground">
                      Blank sends our default message:
                    </p>
                    <p className="italic">{defaultMessage}</p>
                  </div>
                )}
                <FormMessage />
              </FormItem>
            )
          }}
        />
      </CardContent>
    </Card>
  )
}
